"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.TokenService = void 0;
const TokenService_model_1 = require("./TokenService.model");
const Helper_1 = require("../common/Helper");
const JwtHelper_1 = require("../common/JwtHelper");
class TokenService {
    constructor(configUserProvider) {
        this.config = configUserProvider.getConfig();
        this.userManager = configUserProvider.getUserManager();
    }
    /**
     * To generate token(s) with the grant type authorization_code, call **generateTokenFromCode()**
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/4ff850f48629a-generate-token for more details.
     * @example
     * ```js
     * const options = {
     *   code: "your code to be exchanged with the token(s)",
     * }
     *
     * cidaasTokenService.generateTokenFromCode(options)
     *   .then(function (response) {
     *     // type your code here
     *   })
     *   .catch(function (ex) {
     *     // your failure code here
     *   });
     * ```
     */
    generateTokenFromCode(options) {
        return __awaiter(this, void 0, void 0, function* () {
            var _a;
            if (!options.code) {
                throw new Helper_1.CustomException("code cannot be empty", 417);
            }
            options.client_id = this.config.client_id;
            options.redirect_uri = this.config.redirect_uri;
            options.grant_type = TokenService_model_1.GrantType.AuthorizationCode;
            if (!this.config.disablePKCE) {
                const signInRequest = yield this.userManager.getClient().createSigninRequest(this.config);
                options.code_verifier = (_a = signInRequest.state) === null || _a === void 0 ? void 0 : _a.code_verifier;
            }
            const _serviceURL = this.config.authority + "/token-srv/token";
            return Helper_1.Helper.createHttpPromise(options, _serviceURL, undefined, "POST");
        });
    }
    /**
     * To get precheck result after login, call **loginPrecheck()**. If there is missing information, user will be redirected to either accepting consent, changing password, continuing MFA process, or do progressive registration
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/aappczju1t3uh-precheck-information for more details.
     * @example
     * ```js
     * const options = {
     *   track_id: "your track id from login",
     * }
     *
     * cidaasTokenService.loginPrecheck(options)
     * .then(function (response) {
     *   // type your code here
     * })
     * .catch(function (ex) {
     *   // your failure code here
     * });
     * ```
     */
    loginPrecheck(options, headers) {
        const _serviceURL = this.config.authority + "/token-srv/prelogin/metadata/" + options.track_id;
        return Helper_1.Helper.createHttpPromise(undefined, _serviceURL, false, "GET", undefined, headers);
    }
    /**
     * To get the missing fields after login, call **getMissingFields()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/aappczju1t3uh-precheck-information for more details.
     * @example
     * ```js
     * const trackId = "your track id from login";
     * cidaasTokenService.getMissingFields(trackId)
     *   .then(function (response) {
     *     // type your code here
     * })
     *   .catch(function (ex) {
     *     // your failure code here
     * });
     * ```
     */
    getMissingFields(trackId, headers) {
        const _serviceURL = this.config.authority + "/token-srv/prelogin/metadata/" + trackId;
        return Helper_1.Helper.createHttpPromise(undefined, _serviceURL, false, "GET", undefined, headers);
    }
    /**
     * To initiate device code, call **initiateDeviceCode()**.
     * Please refer to the api document https://docs.cidaas.com/docs/cidaas-iam/b6d284f55be5e-authorization-request for more details.
     * @example
     * ```js
     * const clientId = "your client id";
     * cidaasTokenService.initiateDeviceCode(clientId)
     *   .then(function (response) {
     *     // type your code here
     * })
     *   .catch(function (ex) {
     *     // your failure code here
     * });
     * ```
     */
    initiateDeviceCode(clientId) {
        const clientid = clientId !== null && clientId !== void 0 ? clientId : this.config.client_id;
        const _serviceURL = `${this.config.authority}/authz-srv/device/authz?client_id=${clientid}`;
        return Helper_1.Helper.createHttpPromise(undefined, _serviceURL, false, "GET");
    }
    /**
     * To verify device code, call **deviceCodeVerify()**.
     * @example
     * ```js
     * const code = "your code which has been send after initiateDeviceCode()";
     * cidaasTokenService.deviceCodeVerify(code)
     *   .then(function (response) {
     *     // type your code here
     * })
     *   .catch(function (ex) {
     *     // your failure code here
     * });
     * ```
     */
    deviceCodeVerify(code) {
        const params = `user_code=${encodeURI(code)}`;
        const url = `${this.config.authority}/token-srv/device/verify?${params}`;
        try {
            const options = {
                user_code: encodeURI(code)
            };
            const form = Helper_1.Helper.createForm(url, options, 'GET');
            document.body.appendChild(form);
            form.submit();
        }
        catch (ex) {
            throw new Error(String(ex));
        }
    }
    /**
     * To check access token without having to call cidaas api, call **offlineTokenCheck()**. THe function will return true if the token is valid & false if the token is invalid.
     * @example
     * ```js
     * cidaasTokenService.offlineTokenCheck('your access token');
     * ```
     */
    offlineTokenCheck(accessToken) {
        var _a, _b;
        const result = {
            isExpiryDateValid: false,
            isScopesValid: false,
            isIssuerValid: false,
        };
        const accessTokenHeaderAsJson = JwtHelper_1.JwtHelper.decodeTokenHeader(accessToken);
        const accessTokenAsJson = JwtHelper_1.JwtHelper.decodeToken(accessToken);
        if (!accessTokenAsJson || !accessTokenHeaderAsJson) {
            return result;
        }
        else {
            if (accessTokenAsJson.exp) {
                const expirationDate = new Date(0);
                expirationDate.setUTCSeconds(accessTokenAsJson.exp);
                result.isExpiryDateValid = expirationDate.valueOf() > new Date().valueOf();
            }
            const accessTokenScopes = accessTokenAsJson.scopes;
            const configScopes = (_b = (_a = this.config) === null || _a === void 0 ? void 0 : _a.scope) === null || _b === void 0 ? void 0 : _b.split(' ');
            if ((accessTokenScopes === null || accessTokenScopes === void 0 ? void 0 : accessTokenScopes.length) === (configScopes === null || configScopes === void 0 ? void 0 : configScopes.length)) {
                configScopes.forEach(scope => {
                    const i = accessTokenScopes.indexOf(scope);
                    if (i > -1) {
                        accessTokenScopes.splice(i, 1);
                    }
                });
                result.isScopesValid = accessTokenScopes.length === 0;
            }
            result.isIssuerValid = accessTokenAsJson.iss === this.config.authority;
        }
        return result;
    }
}
exports.TokenService = TokenService;
